/**
 * Phone validation + national-number grouping for the widget forms (order + escalation).
 * Both forms use a dial-code <select> + a grouped national input; this module validates the
 * composed "<cc> <national>" value and groups the national part for readability.
 *
 * Per-country length validation (mirror of the server's PhoneValidator):
 *  - a number with a known "+<cc>"/"00<cc>" → its national digit count must fit that country's
 *    [min,max] window (PHONE_LEN). An unlisted country code → E.164-lite (9–15 significant
 *    digits). A BARE national number → validated against `defaultCc` (the bot's country,
 *    falling back to PL +48), generalizing the old PL 9 / 11(48) / 13(0048) rule.
 * The server re-checks everything; the client mirror just avoids a 422 round-trip.
 */

function digitsOf(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** Group a national number in 3s for readability: "600100200" → "600 100 200". Caps the
 *  national part at `maxDigits` (default 12 = E.164 ≤ 15 minus a 3-digit country code).
 *  Callers pass a cc-aware cap: 9 for +48 (PL national is fixed-length), 15−ccDigits else. */
export function groupNational(raw: string, maxDigits = 12): string {
  return digitsOf(raw)
    .slice(0, Math.max(0, maxDigits))
    .replace(/(\d{3})(?=\d)/g, '$1 ');
}

/**
 * National significant-number digit windows [min,max] per dial code. Source: libphonenumber-js
 * 1.13.6 (derived 2026-06-18) — example-mobile length + per-type fixed-line possibleLengths,
 * curated to the mobile+fixed CONTACT range. Uniform-length plans are pinned exact; variable
 * countries (DE/AT/SE/IT/IE/GB) use permissive ranges floored to reject gross junk ("+49 234 33").
 * The general possibleLengths union is deliberately NOT used (it includes premium/short codes,
 * e.g. PL [6,10], which would loosen the strict cases). MUST stay in sync with the server's
 * PhoneValidator::PHONE_LEN — change both together (the shared test vectors guard the drift).
 */
export const PHONE_LEN: Record<string, readonly [number, number]> = {
  '+48': [9, 9], '+49': [7, 12], '+44': [9, 10], '+1': [10, 10],
  '+33': [9, 9], '+34': [9, 9], '+39': [6, 12], '+31': [9, 9],
  '+420': [9, 9], '+421': [9, 9], '+380': [9, 9], '+43': [7, 13],
  '+41': [9, 9], '+32': [8, 9], '+46': [7, 12], '+45': [8, 8],
  '+47': [8, 8], '+353': [7, 10], '+351': [9, 9], '+40': [9, 9],
  '+36': [8, 9], '+370': [8, 8], '+371': [8, 8], '+372': [7, 8],
};
// Sorted by descending code length so "+420" matches before "+4…", "+353" before "+34", etc.
const PHONE_LEN_DESC: ReadonlyArray<readonly [string, readonly [number, number]]> = Object.entries(
  PHONE_LEN,
).sort((a, b) => b[0].length - a[0].length);

const inWindow = (n: number, win: readonly [number, number]): boolean => n >= win[0] && n <= win[1];

/** A bare national number matched against one country: the pure national length, OR the same
 *  number carrying its own cc / 00cc with no "+" (generalizes PL's 9 / 11(48) / 13(0048)). */
function matchesNational(digits: string, cc: string): boolean {
  const win = PHONE_LEN[cc] ?? ([9, 15] as const);
  const cd = digitsOf(cc);
  return (
    inWindow(digits.length, win) ||
    (digits.startsWith(cd) && inWindow(digits.length - cd.length, win)) ||
    (digits.startsWith('00' + cd) && inWindow(digits.length - 2 - cd.length, win))
  );
}

/**
 * Validate a phone number's national length per country. A "+cc"/"00cc" prefix selects the
 * country window (longest known code wins); an unlisted code falls back to E.164-lite (9–15
 * significant digits). A BARE national number is validated against `defaultCc` (the bot's
 * country). Whitespace is normalised so "+ 49" == "+49" (parity with the server).
 */
export function isValidPhone(raw: string, defaultCc = '+48'): boolean {
  const s = raw.replace(/\s+/g, '');
  const digits = digitsOf(s);

  if (s.startsWith('+') || /^00\d/.test(s)) {
    const sig = s.startsWith('00') ? digits.slice(2) : digits; // significant digits incl. country code
    for (const [cc, win] of PHONE_LEN_DESC) {
      const cd = digitsOf(cc);
      if (sig.startsWith(cd)) return inWindow(sig.length - cd.length, win);
    }
    return sig.length >= 9 && sig.length <= 15; // unlisted country code → E.164-lite
  }

  return matchesNational(digits, defaultCc);
}

/** Sentinel <select> value for "country not in the list" — the user then types a full
 *  "+cc national" number into the field (validated via isValidPhone's unlisted-cc path). */
export const OTHER_CC = 'other';

/** Country dial codes for the phone selector (E.164 code + ISO label). PL first.
 *  Text labels (not flag emoji — those render as tofu on Windows/Linux browsers).
 *  Every LOCALE_TO_CC value MUST be one of these codes (guarded by a test) — else
 *  the selector default falls back to the first option. */
export const PHONE_CODES: ReadonlyArray<{ code: string; iso: string }> = [
  { code: '+48', iso: 'PL' }, { code: '+49', iso: 'DE' }, { code: '+44', iso: 'GB' },
  { code: '+1', iso: 'US' }, { code: '+33', iso: 'FR' }, { code: '+34', iso: 'ES' },
  { code: '+39', iso: 'IT' }, { code: '+31', iso: 'NL' }, { code: '+420', iso: 'CZ' },
  { code: '+421', iso: 'SK' }, { code: '+380', iso: 'UA' }, { code: '+43', iso: 'AT' },
  { code: '+41', iso: 'CH' }, { code: '+32', iso: 'BE' }, { code: '+46', iso: 'SE' },
  { code: '+45', iso: 'DK' }, { code: '+47', iso: 'NO' }, { code: '+353', iso: 'IE' },
  { code: '+351', iso: 'PT' }, { code: '+40', iso: 'RO' }, { code: '+36', iso: 'HU' },
  { code: '+370', iso: 'LT' }, { code: '+371', iso: 'LV' }, { code: '+372', iso: 'EE' },
];

/** Widget locale (bot language) → default dial code. Every value must exist in
 *  PHONE_CODES (test-guarded). Falls back to +48 when a locale isn't mapped. */
export const LOCALE_TO_CC: Record<string, string> = {
  pl: '+48', de: '+49', en: '+44', es: '+34', fr: '+33', it: '+39', cs: '+420',
  sk: '+421', uk: '+380', nl: '+31', pt: '+351', ro: '+40', hu: '+36',
};

/** Light sanitize for the free-form "Other country" phone field: keep a single leading "+",
 *  digits + single spaces; drop the rest; cap length ("+" and up to ~15 digits). */
export function formatIntlInput(raw: string): string {
  const hasPlus = /^\s*\+/.test(raw);
  const body = raw
    .replace(/[^\d\s]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^\s+/, '');
  return ((hasPlus ? '+' : '') + body).slice(0, 18);
}

/**
 * Practical email shape — mirror of EmailValidator::isAcceptable's regex (server is
 * the source of truth). Stricter than "anything@anything.anything": common local-part
 * chars + a letter TLD, so junk like "foo!@host" is blocked before the network.
 */
export function isValidEmail(raw: string): boolean {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(raw.trim());
}
